import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { PageLayout } from '@/components/layout/PageLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrgPlugins } from '@/hooks/plugins/useOrgPlugins';
import { useOrgPlugin } from '@/hooks/plugins/useOrgPlugin';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { PluginDetailContent } from './detail/PluginDetailContent';

/**
 * Per-org plugin detail page mounted at
 * `/orgs/${slug}/configure/connect/plugins/:name`.
 * Displays the plugin's status, dependencies, and install / uninstall
 * actions. The action affordances live behind `<CanGuard
 * permission="plugins:install">` so org members see the page in
 * read-only mode.
 *
 * The body content is rendered by `PluginDetailContent`, which is
 * shared with the org-setup wizard's slide-over sheet.
 */
export function PluginDetailPage() {
  const params = useParams<{ pluginName: string }>();
  const pluginName = params.pluginName ?? '';
  const { activeOrganizationId } = useAuth();
  const orgId = activeOrganizationId;
  const orgRoutes = useOrgRoutes();
  const detailQuery = useOrgPlugin(orgId, pluginName);
  const listQuery = useOrgPlugins(orgId);

  return (
    <PageLayout maxWidth="4xl">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={orgRoutes.orgPlugins} aria-label="Back to integrations">
            <ArrowLeft className="h-4 w-4" />
            All integrations
          </Link>
        </Button>
      </div>
      <AsyncContent
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        loadingMessage="Loading plugin…"
        errorMessage="Failed to load plugin."
      >
        {detailQuery.data && (
          <PluginDetailContent row={detailQuery.data} orgId={orgId} allRows={listQuery.data} />
        )}
      </AsyncContent>
    </PageLayout>
  );
}
